import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
  SeverityContainer,
  StyledCircle,
} from 'components/FleetMap/TurbinePointMap/TurbinesList/TurbinesList.Styles';
import { type TInspectionData } from 'components/FleetMap/types';
import { StatusTag } from 'components/StatusTag';
import { type AtlasGqlTurbine } from 'types/atlas-graphql';

type FleetMapDetailAsset = AtlasGqlTurbine & {
  assetTypeName?: string;
};

const LinkLabel = styled.span`
  font-size: 0.9rem;
  min-width: 3rem;
  margin-right: 0.5rem;
`;

const LinkTitle = styled.span`
  font-size: 0.9rem;
`;

const StaticLabel = styled.span`
  font-size: 0.9rem;
  margin: 0;
`;

const RowContainer = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
`;

interface FleetMapDetailProps {
  turbine: FleetMapDetailAsset | null;
  previewMode?: boolean;
  inspectionData?: TInspectionData;
}

const FleetMapDetail: FC<FleetMapDetailProps> = ({ turbine, previewMode, inspectionData }) => {
  const { t } = useTranslation();
  const [lat, lng] = turbine?.latLng ?? [];

  return (
    <>
      {turbine ? (
        <>
          {turbine.location && (
            <RowContainer data-testid="fleet-map-detail">
              <LinkLabel>{t('table.site', 'Site')}:</LinkLabel>
              {previewMode ? (
                <StaticLabel>{turbine.location.name}</StaticLabel>
              ) : (
                <Link
                  to={`/sites/${turbine.location.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkTitle>{turbine.location.name}</LinkTitle>
                </Link>
              )}
            </RowContainer>
          )}
          <RowContainer data-testid="fleet-map-detail">
            <LinkLabel>{turbine.assetTypeName ?? t('table.turbine', 'Turbine')}:</LinkLabel>
            {previewMode ? (
              <StaticLabel>{turbine.name}</StaticLabel>
            ) : (
              <Link to={`/turbines/${turbine.id}`} target="_blank" rel="noopener noreferrer">
                <LinkTitle>{turbine.name}</LinkTitle>
              </Link>
            )}
          </RowContainer>
          {previewMode ? (
            <>
              <RowContainer>
                <LinkLabel />
                {turbine.status ? <StatusTag value={turbine.status} /> : null}
              </RowContainer>
              <RowContainer>
                <LinkLabel>{t('table.lat', 'Lat')}:</LinkLabel>
                <StaticLabel>{lat}</StaticLabel>
              </RowContainer>
              <RowContainer>
                <LinkLabel>{t('table.lng', 'Lng')}:</LinkLabel>
                <StaticLabel>{lng}</StaticLabel>
              </RowContainer>
            </>
          ) : (
            <>
              {inspectionData?.id && inspectionData?.inspectionDate && (
                <RowContainer data-testid="fleet-map-inspection-date">
                  <LinkLabel>
                    {t('table.mostRecentInspection', 'Most Recent Inspection')}:
                  </LinkLabel>
                  <Link
                    to={`/inspections/${inspectionData.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkTitle>{inspectionData.inspectionDate}</LinkTitle>
                  </Link>
                </RowContainer>
              )}
              {inspectionData?.maxSeverity && (
                <RowContainer data-testid="fleet-map-max-severity">
                  <LinkLabel>{t('table.maxSeverity', 'Max Severity')}: </LinkLabel>
                  <SeverityContainer>
                    <StyledCircle $severity={inspectionData.maxSeverity} />
                    <LinkTitle>{inspectionData.maxSeverity}</LinkTitle>
                  </SeverityContainer>
                </RowContainer>
              )}
            </>
          )}
        </>
      ) : null}
    </>
  );
};

export default FleetMapDetail;
